<template>
  <AppLayout centered customToolbar>
    <template v-slot:body>
      <BackBar parent="/settings/organisation" title="Organisation Settings" />
      <div class="containter" v-if="isFetchingUnits">
        <v-skeleton-loader type="list-item" />
      </div>
      <div class="d-flex justify-center flex-wrap mb-5 px-6" v-else>
        <v-btn icon color="primary" @click="viewOrgUnits">
          <v-icon>mdi-cube-outline</v-icon>
        </v-btn>
        <h2 class="secondary-text">/</h2>
        <div class="d-flex" v-for="parent in parents" :key="parent.id">
          <v-btn text color="primary" @click="viewOrgUnit(parent.id)">
            {{ parent.name | startCase }}
          </v-btn>
          <h2 class="secondary-text">/</h2>
        </div>
        <h2 class="mx-2">{{ unitName }}</h2>
        <h2 class="secondary-text">{{ costCode }}</h2>
      </div>
      <div class="text-center">
        <v-btn-toggle
          class="organisation-section-buttons mb-4"
          v-model="selectedTab"
          tile
          group
          mandatory
          @change="handleSectionChange"
        >
          <v-btn
            v-for="(section, sectionKey, index) in sections"
            :key="sectionKey"
            :value="index"
          >
            {{ section }}
          </v-btn>
        </v-btn-toggle>
      </div>
      <router-view
        :key="selectedTab"
        :unit="selectedUnit"
        :parent="parent"
        :searchText="searchText"
      />
    </template>
  </AppLayout>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { last, find, isEmpty } from "lodash";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { copyState } from "@/utils/helpers";
import {
  getUnitsWithParents,
  getOrganisationsAsTreeStructure,
  findDeep
} from "@/utils/organisation-units";
import AppLayout from "@/components/common/base-layouts/AppLayout";
import BackBar from "@/components/common/base-layouts/BackBar";
import { FETCH_ORGANISATION_UNITS } from "@/store/modules/organisations/organisation-units/actions";
import { ORGANISATION_UNITS_NAMESPACE } from "@/store/modules/organisations/organisation-units";

const {
  mapActions,
  mapState: mapOrganisationUnitsState
} = createNamespacedHelpers(ORGANISATION_UNITS_NAMESPACE);

const { mapState } = createNamespacedHelpers(AUTH_NAMESPACE);

export default {
  components: { AppLayout, BackBar },
  mounted() {
    this.selectedTab = this.initialSection;
    if (isEmpty(this.organisationUnits)) {
      this.fetchOrganisationUnits({});
    }
  },
  data() {
    return {
      selectedTab: 0,
      searchText: ""
    };
  },
  methods: {
    ...mapActions({
      fetchOrganisationUnits: FETCH_ORGANISATION_UNITS
    }),
    handleSectionChange(selectedTab) {
      const sectionKey = Object.keys(this.sections)[selectedTab];
      this.$router.replace(
        `/settings/organisation/${this.unitId}/${sectionKey}`
      );
    },
    viewOrgUnits() {
      this.$router.push({ path: `/settings/organisation` });
    },
    viewOrgUnit(id) {
      this.$router.push({
        path: `/settings/organisation/${id}`
      });
    }
  },
  computed: {
    ...mapState({
      permissions: state => state.permissions
    }),
    ...mapOrganisationUnitsState(
      copyState(["organisationUnits", "isFetchingUnits"])
    ),
    initialSection() {
      const sectionKey = last(this.$router.currentRoute.path.split("/"));
      return Object.keys(this.sections).indexOf(sectionKey);
    },
    unitsWithParents() {
      return getUnitsWithParents(this.organisationUnits);
    },
    unitWithParents() {
      return find(this.unitsWithParents, { id: this.unitId });
    },
    parents() {
      return isEmpty(this.unitWithParents) ? [] : this.unitWithParents.parents;
    },
    parent() {
      return !isEmpty(this.parents) && this.unitWithParents
        ? find(this.parents, { id: this.unitWithParents.parentId })
        : {};
    },
    costCode() {
      return this.selectedUnit && this.selectedUnit.costCode
        ? `(${this.selectedUnit.costCode})`
        : "";
    },
    unitName() {
      return this.selectedUnit ? `${this.selectedUnit.name}` : "";
    },
    unitId() {
      return this.$route.params.id;
    },
    selectedUnit() {
      return findDeep(this.filteredUnits, this.unitId);
    },
    filteredUnits() {
      return getOrganisationsAsTreeStructure(this.organisationUnits);
    },
    sections() {
      return {
        ...(this.permissions["settings.edit-organisation-unit"] && {
          units: "Org Units"
        }),
        ...(this.permissions["settings.edit-organisation-unit"] && {
          users: "Scoped Users"
        })
      };
    }
  }
};
</script>
